import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Modal, Alert, Form, Card } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import PageTitle from '@/common/PageTitle/PageTitle';
import styles from './inventory-product-details.module.scss';

// Product data that matches the products from inventory-products.tsx
const productsData = {
  '1': {
    id: '1',
    name: 'KrossKast Player 8',
    brand: 'KrossKast',
    category: 'KK Players',
    type: 'Electronic Device',
    model: 'KKP-8',
    image: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=facearea&w=400&h=400',
    code: 'KKP-8-001',
    orderName: 'KKP8-001',
    wearhouseAvailability: [
      { name: 'Georgia, US', available: true },
      { name: 'Florida, US', available: true },
      { name: 'Texas, US', available: false },
      { name: 'Arizona, US', available: true },
    ],
    lastUpdate: 'January 29, 2023 at 2:39PM',
    related: [
      { name: 'KrossKast Player 11', image: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=facearea&w=400&h=400' },
    ],
  },
  '2': {
    id: '2',
    name: 'KrossKast Player 11',
    brand: 'KrossKast',
    category: 'KK Players',
    type: 'Electronic Device',
    model: 'KKP-11',
    image: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=facearea&w=400&h=400',
    code: 'KKP-11-001',
    orderName: 'KKP11-001',
    wearhouseAvailability: [
      { name: 'Georgia, US', available: true },
      { name: 'Florida, US', available: true },
      { name: 'Texas, US', available: true },
      { name: 'Arizona, US', available: false },
    ],
    lastUpdate: 'January 29, 2023 at 2:39PM',
    related: [
      { name: 'KrossKast Player 8', image: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=facearea&w=400&h=400' },
    ],
  },
};

const ProductDetails: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const baseProduct = productsData[productId as keyof typeof productsData];
  
  
  // State for current product data (allows for real-time updates)
  const [currentProduct, setCurrentProduct] = useState(baseProduct);
  const product = currentProduct;

  // Update product when URL changes
  useEffect(() => {
    const newProduct = productsData[productId as keyof typeof productsData];
    if (newProduct) {
      setCurrentProduct(newProduct);
    }
  }, [productId]);

  // State for modals and alerts
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  
  // State for edit form
  const [editForm, setEditForm] = useState({
    name: product?.name || '',
    brand: product?.brand || '',
    type: product?.type || '',
    model: product?.model || '',
    category: product?.category || '',
    code: product?.code || '',
    orderName: product?.orderName || '',
    wearhouseAvailability: product?.wearhouseAvailability || [],
  });

  // Handle delete confirmation
  const handleDelete = () => {
    // In a real app, this would make an API call to delete the product
    console.log('Deleting product:', product?.name);
    setShowDeleteModal(false);
    setAlertMessage(`Product "${product?.name}" has been deleted successfully.`);
    setShowSuccessAlert(true);
    
    // Navigate back to products list after a short delay
    setTimeout(() => {
      navigate('/inventory-products');
    }, 2000);
  };

  // Handle edit form changes
  const handleEditFormChange = (field: string, value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle warehouse availability changes
  const handleWarehouseChange = (locationName: string, available: boolean) => {
    setEditForm(prev => ({
      ...prev,
      wearhouseAvailability: prev.wearhouseAvailability.map(wh => 
        wh.name === locationName ? { ...wh, available } : wh
      )
    }));
  };

  // Handle edit form submission
  const handleEditSubmit = () => {
    // In a real app, this would make an API call to update the product
    console.log('Updating product:', editForm);
    
    // Update the current product state with the new data
    setCurrentProduct(prev => ({
      ...prev,
      name: editForm.name,
      brand: editForm.brand,
      type: editForm.type,
      model: editForm.model,
      category: editForm.category,
      code: editForm.code,
      orderName: editForm.orderName,
      wearhouseAvailability: editForm.wearhouseAvailability,
    }));
    
    setShowEditModal(false);
    setAlertMessage(`Product "${editForm.name}" has been updated successfully!`);
    setShowSuccessAlert(true);
    
    // Reset form to updated product data
    setEditForm({
      name: editForm.name,
      brand: editForm.brand,
      type: editForm.type,
      model: editForm.model,
      category: editForm.category,
      code: editForm.code,
      orderName: editForm.orderName,
      wearhouseAvailability: editForm.wearhouseAvailability,
    });
  };

  // Reset edit form when modal opens
  const handleEditClick = () => {
    setEditForm({
      name: product?.name || '',
      brand: product?.brand || '',
      type: product?.type || '',
      model: product?.model || '',
      category: product?.category || '',
      code: product?.code || '',
      orderName: product?.orderName || '',
      wearhouseAvailability: product?.wearhouseAvailability || [],
    });
    setShowEditModal(true);
  };

  if (!product) {
    return (
      <Container fluid className={styles.container}>
        <h2 className={styles.pageTitle}>Product not found</h2>
        <div>Sorry, the product you're looking for doesn't exist.</div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Card className={styles.card}>
        <Card.Body>
          <Card.Title className={styles.cardTitle}>
            <PageTitle title={`Product Details: ${product.name}`} />
            <div className={styles.lastUpdate}>
              Last update {product.lastUpdate}
            </div>
          </Card.Title>
          
          {/* Success Alert */}
          {showSuccessAlert && (
            <Alert 
              variant="success" 
              onClose={() => setShowSuccessAlert(false)} 
              dismissible
              className="mb-3"
            >
              {alertMessage}
            </Alert>
          )}
          
          <div className={styles.productCard}>
        <Row>
          <Col md={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={product.image} alt={product.name} className={styles.productImage} />
          </Col>
          <Col md={5} className={styles.productInfo}>
            <div className={styles.productName}>{product.name}</div>
            <div className={styles.productBrand}>{product.brand}</div>
            <div className={styles.productDetail}>
              Type: <span className={styles.value}>{product.type}</span>
            </div>
            <div className={styles.productDetail}>
              Model: <span className={styles.value}>{product.model}</span>
            </div>
            <div className={styles.productDetail}>
              Category: <span className={styles.value}>{product.category}</span>
            </div>
          </Col>
          <Col md={4} className={styles.productActions}>
            <div className={styles.buttonGroup}>
              <Button 
                onClick={() => setShowDeleteModal(true)}
                style={{ 
                  background: 'white', 
                  color: '#333', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: '500',
                  marginRight: '8px'
                }}
              >
                Delete
              </Button>
              <Button 
                onClick={handleEditClick}
                style={{ 
                  background: 'white', 
                  color: '#333', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: '500',
                  marginRight: '8px',
                  minWidth: '140px'
                }}
              >
                Edit Product
              </Button>
              <Button 
                onClick={() => navigate(`/inventory-products/${productId}/inventory`)}
                style={{ 
                  background: 'white', 
                  color: '#333', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: '500',
                  minWidth: '180px'
                }}
              >
                Product Inventory
              </Button>
            </div>
          </Col>
        </Row>
        <Row style={{ marginTop: 32 }}>
          <Col md={3} className={styles.qrSection}>
            <div className={styles.qrCode}>
              {/* Placeholder for QR code */}
              <span className={styles.qrPlaceholder}>▣▣▣▣</span>
            </div>
            <div className={styles.productCode}>Product code: {product.code}</div>
            <div className={styles.orderName}>Order name: {product.orderName}</div>
          </Col>
          <Col md={5}></Col>
          <Col md={4} className={styles.warehouseSection}>
            <div className={styles.warehouseTitle}>Wearhouse availability:</div>
            <div>
              {product.wearhouseAvailability.map((wh) => (
                <div key={wh.name} className={styles.warehouseItem}>
                  {wh.name}
                  {wh.available ? (
                    <span className={styles.warehouseAvailable}>✔</span>
                  ) : (
                    <span className={styles.warehouseUnavailable}>✘</span>
                  )}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </div>
      <div className={styles.relatedTitle}>Related products</div>
      <div className={styles.relatedProducts}>
        {product.related.length === 0 ? (
          <div>No related products found</div>
        ) : (
          product.related.map((rel) => {
            // Find the related product's ID from the productsData
            const relatedProductId = Object.keys(productsData).find(
              id => productsData[id as keyof typeof productsData].name === rel.name
            );
          
          return (
            <div 
              key={rel.name} 
              className={`${styles.relatedProduct} ${styles.clickable}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (relatedProductId && relatedProductId !== productId) {
                  navigate(`/inventory-products/${relatedProductId}`);
                }
              }}
            >
              <img 
                src={rel.image} 
                alt={rel.name} 
                className={styles.relatedImage}
              />
              <div className={styles.relatedName}>
                {rel.name}
              </div>
            </div>
          );
        })
        )}
          </div>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>{product.name}</strong>?</p>
          <p className="text-muted">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete Product
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Product Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Product: {product.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Product Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.name}
                    onChange={(e) => handleEditFormChange('name', e.target.value)}
                    placeholder="Enter product name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Brand <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.brand}
                    onChange={(e) => handleEditFormChange('brand', e.target.value)}
                    placeholder="Enter brand name"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.type}
                    onChange={(e) => handleEditFormChange('type', e.target.value)}
                    placeholder="e.g., Electronic Device"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Model <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.model}
                    onChange={(e) => handleEditFormChange('model', e.target.value)}
                    placeholder="e.g., KKP-8"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={editForm.category}
                    onChange={(e) => handleEditFormChange('category', e.target.value)}
                  >
                    <option value="">Select category</option>
                    <option value="KK Players">KK Players</option>
                    <option value="T-Shirts">T-Shirts</option>
                    <option value="Sweaters">Sweaters</option>
                    <option value="Promotional">Promotional</option>
                    <option value="Hats">Hats</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Product Code <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.code}
                    onChange={(e) => handleEditFormChange('code', e.target.value)}
                    placeholder="e.g., KKP-8-001"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Order Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.orderName}
                    onChange={(e) => handleEditFormChange('orderName', e.target.value)}
                    placeholder="e.g., KKP8-001"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            {/* Warehouse Availability Section */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Warehouse Availability</Form.Label>
                  <div className={styles.warehouseEditSection}>
                    {editForm.wearhouseAvailability.map((warehouse) => (
                      <div key={warehouse.name} className={styles.warehouseEditItem}>
                        <div className={styles.warehouseLocation}>{warehouse.name}</div>
                        <div className={styles.warehouseToggle}>
                          <Form.Check
                            type="switch"
                            id={`warehouse-${warehouse.name}`}
                            label={warehouse.available ? "Available" : "Unavailable"}
                            checked={warehouse.available}
                            onChange={(e) => handleWarehouseChange(warehouse.name, e.target.checked)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProductDetails; 